// Single authoritative enrollment source for the CLIENT.
//
// Layer map (deliberately three layers, not one — do not merge them):
//   • enrollment.ts (this file) — primitives: who legitimately occupies a seat
//     (`countsAsEnrolled`), who counts toward a PAID minimum
//     (`countsTowardPaidMinimum`), roster/session matching, interest families.
//   • operationalEnrollment.ts — applies the current/upcoming canonical session
//     rule + child-level de-duplication on top of those primitives.
//   • classTruth.ts — cross-surface read layer that composes the above with
//     instructor + session anchors so every admin page shows the same numbers.
//
// Distinct concepts that must NEVER be collapsed:
//   occupied = legitimate registered seats INCLUDING comped/$0 seats
//   paid     = qualifying paid subset (paid minimums, enrollment-tiered pay)
//   interest = submitted interest forms, never enrollment
//
// The edge counterpart `supabase/functions/_shared/enrollment-resolver.ts`
// intentionally mirrors these rules server-side (Deno runtime, service-role
// data, no browser bundle). It is the enforcement copy; this is the display
// copy. Any rule change must be made in BOTH, and both are covered by tests.
//
// Cancelled / refunded / failed / hidden / interest / waitlist rows do NOT count.


import { supabase } from "@/integrations/supabase/client";
import { getClassAliases } from "@/lib/classIdentity";

export interface RegistrationRow {
  id: string;
  class_id: string;
  adult_name: string | null;
  child_name: string | null;
  email: string | null;
  selected_sessions: unknown;
  amount_paid: number | null;
  payment_status: string | null;
  hidden: boolean | null;
  created_at: string;
  stripe_session_id?: string | null;
  refunded_amount_cents?: number | null;
  is_test_record?: boolean | null;
  removed_at?: string | null;
  /**
   * Non-destructive operational exception (e.g. `august_chess_not_offered`).
   * The record stays fully preserved — sessions, amount, payment linkage —
   * but contributes ZERO seats to any active roster or capacity count.
   */
  operational_exception?: string | null;
  operational_exception_note?: string | null;
  canonical_session_label?: string | null;
  /** Full tuition owed for this registration (independent of what was paid). */
  tuition_total_cents?: number | null;
  /** Value transferred in from another paid registration. NOT a discount. */
  transfer_credit_cents?: number | null;
  /** Cash still outstanding after transferred credit. */
  balance_due_cents?: number | null;
  transfer_source_payment_id?: string | null;
  admin_audit_note?: string | null;
  /** Authorized free seat (owner-approved comp or 100% coupon). Occupies capacity, never paid. */
  is_complimentary?: boolean | null;
  complimentary_reason?: string | null;
  /** Coupon / free-code identifier preserved for audit. */
  discount_code?: string | null;
  discount_cents?: number | null;
  original_price_cents?: number | null;
}


export interface RegistrationChildRow {
  id: string;
  class_payment_id: string;
  class_id: string | null;
  session_label: string | null;
  child_first_name: string | null;
  child_last_name: string | null;
  child_age: number | null;
  grade: string | null;
  notes: string | null;
  final_child_amount?: number | null;
  tuition_total_cents?: number | null;
  transfer_credit_cents?: number | null;
  balance_due_cents?: number | null;
  created_at: string;
}

export interface WaitlistRow {
  id: string;
  class_id: string;
  adult_name: string;
  child_name: string;
  email: string;
  created_at: string;
}

/**
 * Manual per-session reservations — mirror `_shared/capacity-policy.ts`.
 *
 * Keys must identify the session OCCURRENCE, not just the reusable label: a
 * bare "Session 1" key applies to every future occurrence of that label. Seats
 * from a past term use that term's archived occurrence label.
 *
 * Retired: "mommy-me-baking-2|Session 1": 4 — those four off-platform seats
 * belonged to the MAY 2026 occurrence and were filling October 2026's
 * "Session 1", blocking checkout. Preserved against the archived May
 * occurrence; they must never apply to a later "Session 1".
 */
export const MANUAL_SESSION_RESERVATIONS: Record<string, number> = {
  "mommy-me-baking-2|Session 1 · May 2026 (archived)": 4,
  "mommy-me-crochet|Session 1": 10,
  "mommy-me-crochet|Session 2": 10,
};

/** Manual whole-class reservations — for classes without sessions. */
export const MANUAL_RESERVATIONS: Record<string, number> = {
  "pokemon-club": 2,
  "mommy-me-crochet": 2,
};

export const DEFAULT_CAPACITY = 10;

/**
 * CANONICAL CAPACITY AUTHORITY (client mirror of
 * `supabase/functions/_shared/capacity-policy.ts`).
 *
 * An explicitly configured class/session capacity always wins. The standard
 * seat limit is only a fallback for classes that have never had one set.
 * Never hard-code a class slug here — configuration lives in the Class Editor.
 */
export function resolveCapacity(configured?: number | null): number {
  const n = Number(configured);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : DEFAULT_CAPACITY;
}

/**
 * Normalize a session label to a canonical form. Collapses variants like
 * "session 1", "Session 1", "s1", "1", "sess. 1" into "Session 1" so
 * duplicate registration rows never render as duplicate sessions.
 * Non-numeric labels (e.g. "Fall Semester", "Wednesdays (August)") are
 * trimmed but otherwise preserved as-is.
 */
export function normalizeSessionLabel(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const compact = s.toLowerCase().replace(/[\s._-]+/g, "");
  const m = compact.match(/^(?:session|sess|s)?(\d{1,2})$/);
  if (m) return `Session ${parseInt(m[1], 10)}`;
  return s;
}

/**
 * Resolve any raw session/schedule label into two orthogonal axes:
 * `period` (May/June/July/August/Fall Semester/Spring Semester/Unspecified)
 * and `option` (weekday, section, or time within the period).
 *
 * Every dashboard groups: period → class → schedule option → records.
 * This is the single source of truth. Do not parse display strings elsewhere.
 */
export type EnrollmentPeriod =
  | "May" | "June" | "July" | "August"
  | "Fall Semester" | "Spring Semester" | "Unspecified";

export interface ResolvedScheduleAxis {
  period: EnrollmentPeriod;
  option: string | null;
  raw: string;
}

const MONTHS = ["january","february","march","april","may","june","july","august","september","october","november","december"];
const SUMMER_MONTHS: Record<string, EnrollmentPeriod> = { may: "May", june: "June", july: "July", august: "August" };

/** Chronological ordering used when a label covers several periods. */
const PERIOD_RANK: Record<EnrollmentPeriod, number> = {
  Unspecified: -1, May: 0, June: 1, July: 2, August: 3,
  "Fall Semester": 4, "Spring Semester": 5,
};

export function resolveEnrollmentPeriodAndScheduleOption(
  raw: string | null | undefined,
): ResolvedScheduleAxis {
  // Decorative canonical text ("· Jun 2026 (archived)") is generated from the
  // REGISTRATION date, not the session date. It must never override the real
  // purchased session, so it is stripped before any period inference.
  const undecorated = stripSessionDecoration(raw) ?? String(raw ?? "").trim();

  // Multi-session labels ("Session 3 + Session 4") resolve to their latest
  // component here; callers that need every component use resolveAllPeriods.
  if (/\s\+\s/.test(undecorated)) {
    const parts = undecorated.split(/\s\+\s/).map((s) => s.trim()).filter(Boolean);
    const resolvedParts = parts.map((p) => resolveEnrollmentPeriodAndScheduleOption(p));
    const best = resolvedParts
      .filter((r) => r.period !== "Unspecified")
      .sort((a, b) => PERIOD_RANK[b.period] - PERIOD_RANK[a.period])[0];
    if (best) return { ...best, raw: undecorated };
  }

  const label = normalizeSessionLabel(undecorated) ?? "";
  if (!label) return { period: "Unspecified", option: null, raw: "" };


  const sess = label.match(/^Session\s+(\d+)$/i);
  if (sess) {
    const n = parseInt(sess[1], 10);
    const map: Record<number, EnrollmentPeriod> = { 1: "May", 2: "June", 3: "July", 4: "August" };
    return { period: map[n] ?? "Unspecified", option: null, raw: label };
  }

  const lower = label.toLowerCase();
  if (/\bfall\b/.test(lower)) {
    const opt = strip(label, [/\bfall(\s+semester)?\b/i]);
    return { period: "Fall Semester", option: opt || null, raw: label };
  }
  if (/\bspring\b/.test(lower)) {
    const opt = strip(label, [/\bspring(\s+semester)?\b/i]);
    return { period: "Spring Semester", option: opt || null, raw: label };
  }

  for (const m of MONTHS) {
    // Full month names plus common abbreviations ("Oct", "Sept", "Nov.").
    const abbr = m === "september" ? "sept?" : m.slice(0, 3);
    if (!new RegExp(`\\b(?:${m}s?|${abbr})\\b`, "i").test(lower)) continue;
    const cap = m.charAt(0).toUpperCase() + m.slice(1);
    const summer = SUMMER_MONTHS[m];
    const opt = strip(label, [
      new RegExp(`\\(\\s*${cap}s?\\s*\\)`, "i"),
      new RegExp(`\\b${cap}s?\\b`, "i"),
    ]);
    if (summer) return { period: summer, option: opt || null, raw: label };
    const idx = MONTHS.indexOf(m);
    const season: EnrollmentPeriod = idx >= 8 || idx <= 3 ? (idx >= 8 ? "Fall Semester" : "Spring Semester") : "Unspecified";
    return { period: season, option: opt || null, raw: label };
  }

  return { period: "Unspecified", option: label, raw: label };
}

/**
 * Extract a canonical weekday name from a raw session label, if present.
 * Returns e.g. "Wednesday" for "Wednesdays (August)", "Wednesday - August",
 * "Wed 10am", etc. Returns null when no weekday is present.
 */
const WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"] as const;
export function deriveWeekday(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  for (const day of WEEKDAYS) {
    const stem = day.toLowerCase();
    if (new RegExp(`\\b${stem}s?\\b`, "i").test(s)) return day;
  }
  const abbr: Record<string, string> = {
    sun: "Sunday", mon: "Monday", tue: "Tuesday", tues: "Tuesday",
    wed: "Wednesday", weds: "Wednesday", thu: "Thursday", thur: "Thursday",
    thurs: "Thursday", fri: "Friday", sat: "Saturday",
  };
  for (const [k, v] of Object.entries(abbr)) {
    if (new RegExp(`\\b${k}\\b`, "i").test(s)) return v;
  }
  return null;
}


function strip(label: string, patterns: RegExp[]): string {
  let out = label;
  for (const p of patterns) out = out.replace(p, " ");
  return out.replace(/[\s\-–—,()]+/g, " ").trim();
}

/** Stable grouping key for a class + normalized session. */
export function sessionKey(classId: string, sessionLabel: string | null): string {
  return `${classId}|${normalizeSessionLabel(sessionLabel) ?? ""}`;
}

export function parseSelectedSessions(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p.map(String);
    } catch {
      return [raw];
    }
  }
  return [];
}

/**
 * Strip canonical decoration from a session label:
 * "Session 2 · Jun 2026 (archived)" -> "Session 2".
 *
 * DISPLAY / GROUPING ONLY. Never use the stripped form to decide whether a
 * registration occupies a seat — see `sessionLabelMatches`.
 */
export function stripSessionDecoration(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/\s*\(archived\)\s*$/i, "");
  s = s.split("·")[0];
  return s.trim() || null;
}

/**
 * True when a label identifies an ARCHIVED occurrence of a session, e.g.
 * "Session 1 · May 2026 (archived)". Session labels such as "Session 1" are
 * reused every term, so the archive decoration is the only thing that
 * distinguishes last term's occurrence from the current one.
 */
export function isArchivedSessionLabel(raw: string | null | undefined): boolean {
  return /\(archived\)\s*$/i.test(String(raw ?? "").trim());
}

/** Full canonical identity of a session occurrence, decoration included. */
function sessionIdentity(raw: string | null | undefined): string | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  return s.replace(/\s+/g, " ").toLowerCase();
}

/**
 * CAPACITY-SAFE session matching — occurrence identity, not label text.
 *
 * Reusable labels ("Session 1") recur every term, so matching on the bare
 * label alone let May 2026's paid seats refill October 2026's session and
 * blocked checkout. Rules:
 *   • an ARCHIVED occurrence only ever matches that same archived occurrence;
 *   • a current/undecorated target is never satisfied by an archived row;
 *   • otherwise exact canonical identity, with no month inference.
 */
export function sessionLabelMatches(raw: string | null | undefined, target: string | null | undefined): boolean {
  const wanted = normalizeSessionLabel(stripSessionDecoration(target));
  if (!wanted) return true;

  const rawArchived = isArchivedSessionLabel(raw);
  const targetArchived = isArchivedSessionLabel(target);
  // A past occurrence and a current occurrence are different sessions even
  // when they share a label. Historical rows stay preserved — they simply
  // resolve to their own archived occurrence.
  if (rawArchived !== targetArchived) return false;
  if (rawArchived) return sessionIdentity(raw) === sessionIdentity(target);

  const actual = normalizeSessionLabel(stripSessionDecoration(raw));
  if (!actual) return false;
  return actual.toLowerCase() === wanted.toLowerCase();
}

export function isPaid(status: string | null | undefined): boolean {
  const v = (status ?? "").toLowerCase();
  // `partially_refunded` is still a PAID state: the family paid, and only part
  // of the money came back. Seat release is decided exclusively by
  // isFullyRefunded() (amount-based), never by payment_status.
  return v === "paid" || v === "complete" || v === "partially_refunded";
}

/**
 * A legitimate registration on a FREE operating model.
 *
 * Free clubs (pricing_model = free) never touch Stripe, so their registrations
 * are recorded with a non-Stripe settlement status. Such a registration is a
 * real registration: it occupies a physical seat exactly like a paid one. It
 * simply contributes $0 revenue and never counts toward the paid minimum
 * (see `countsTowardPaidMinimum`).
 *
 * This is deliberately a CLOSED vocabulary — `pending`, `failed`, `abandoned`,
 * `superseded` and `refunded` are NOT free registrations.
 */
export function isFreeRegistration(row: Pick<RegistrationRow, "payment_status">): boolean {
  const v = (row.payment_status ?? "").toLowerCase().trim();
  return v === "not_required" || v === "free" || v === "waived" || v === "no_charge";
}


/**
 * A registration funded by value transferred from another paid registration,
 * with cash still outstanding. The Hub already holds this family's money — the
 * seat is real, it simply has a balance. This is never a discount or a comp.
 */
export function isTransferFunded(
  row: Pick<RegistrationRow, "payment_status" | "transfer_credit_cents">,
): boolean {
  return (
    (row.payment_status ?? "").toLowerCase() === "balance_due" &&
    Number(row.transfer_credit_cents ?? 0) > 0
  );
}

/** True when the registration has been refunded in full (seat is released). */
export function isFullyRefunded(row: Pick<RegistrationRow, "amount_paid" | "refunded_amount_cents">): boolean {
  const refunded = Number(row.refunded_amount_cents ?? 0);
  if (refunded <= 0) return false;
  const paid = Number(row.amount_paid ?? 0);
  if (paid <= 0) return true;
  return refunded >= paid;
}

/**
 * True iff this registration counts toward capacity.
 * Excludes hidden, test, removed, non-paid and fully-refunded rows.
 * Partial refunds keep the seat (a partial refund is not a withdrawal).
 *
 * NOTE: an authorized complimentary / 100%-coupon registration DOES occupy a
 * physical seat and therefore counts here. It does NOT count toward the paid
 * minimum-enrollment policy — see `countsTowardPaidMinimum`.
 */
export function countsAsEnrolled(row: RegistrationRow): boolean {
  if (row.hidden) return false;
  if (row.is_test_record) return false;
  if (row.removed_at) return false;
  if (row.operational_exception) return false;
  if (!isPaid(row.payment_status) && !isTransferFunded(row) && !isFreeRegistration(row))
    return false;

  if (isFullyRefunded(row)) return false;
  return true;
}

/**
 * Canonical PAID-seat predicate for the Silver Sage minimum-enrollment policy.
 *
 * The 5-student minimum is a financial viability test, so it counts only seats
 * backed by real tuition for the session in question. A seat counts only when
 * it already counts toward capacity AND represents money the Hub holds.
 *
 * Excluded (still occupies capacity, never counts as paid):
 *   • authorized complimentary registrations (`is_complimentary`)
 *   • 100% coupon / free-code registrations ($0 after discount)
 *   • any other $0 registration
 * Excluded by `countsAsEnrolled` already: test, hidden, removed, unpaid,
 * fully-refunded, and operational-exception rows.
 *
 * Transfer-funded seats DO count: the Hub is holding that family's money and
 * the registration carries a real tuition obligation.
 */
export function countsTowardPaidMinimum(row: RegistrationRow): boolean {
  if (!countsAsEnrolled(row)) return false;
  if (row.is_complimentary) return false;
  if (isAuthorizedCouponRegistration(row)) return false;
  if (isTransferFunded(row)) return true;
  const paid = Number(row.amount_paid ?? 0);
  const refunded = Number(row.refunded_amount_cents ?? 0);
  return paid - refunded > 0;
}

/**
 * Canonical recognition of an authorized 100%-coupon / free-code registration.
 *
 * A registration qualifies ONLY through coupon metadata — never a parent name —
 * so an unrelated $0 row is never mistaken for someone's authorized benefit:
 *   • it carries a coupon / free-code identifier (`discount_code`), AND
 *   • the discount covers the full original price (or nothing was collected).
 *
 * Such a seat occupies physical capacity and is excluded from the paid
 * minimum-enrollment count, exactly like an explicit complimentary seat.
 */
export function isAuthorizedCouponRegistration(row: RegistrationRow): boolean {
  const code = String(row.discount_code ?? "").trim();
  if (!code) return false;
  const original = Number(row.original_price_cents ?? 0);
  const discount = Number(row.discount_cents ?? 0);
  const paid = Number(row.amount_paid ?? 0);
  const refunded = Number(row.refunded_amount_cents ?? 0);
  if (original > 0 && discount >= original) return true;
  return paid - refunded <= 0;
}



/**
 * Roster for a specific class + session label. Returns paid, enrolled
 * registrations only. If `sessionLabel` is null the class has no session
 * granularity — return every counted row for the class.
 */
export function rosterFor(
  payments: RegistrationRow[],
  classId: string,
  sessionLabel: string | null,
  children: RegistrationChildRow[] = [],
): RegistrationRow[] {
  const aliases = new Set(getClassAliases(classId));
  const paymentById = new Map(payments.map((p) => [p.id, p]));
  const normalizedChildren = children.filter((child) => {
    const parent = paymentById.get(child.class_payment_id);
    if (!parent || !countsAsEnrolled(parent)) return false;
    const childClassMatches = child.class_id ? aliases.has(String(child.class_id)) : false;
    const parentClassMatches = aliases.has(String(parent.class_id));
    if (!childClassMatches && !parentClassMatches) return false;
    return sessionLabelMatches(child.session_label, sessionLabel);
  });
  const paymentsWithChildren = new Set(normalizedChildren.map((child) => child.class_payment_id));

  const childRoster = normalizedChildren.map((child) => {
    const parent = paymentById.get(child.class_payment_id);
    const childName = [child.child_first_name, child.child_last_name].filter(Boolean).join(" ").trim();
    return {
      id: child.id,
      class_id: child.class_id || parent?.class_id || classId,
      adult_name: parent?.adult_name ?? null,
      child_name: childName || (parent?.child_name ?? null),
      email: parent?.email ?? null,
      selected_sessions: child.session_label ? [child.session_label] : parent?.selected_sessions ?? null,
      amount_paid: child.final_child_amount ?? parent?.amount_paid ?? null,
      payment_status: parent?.payment_status ?? null,
      hidden: parent?.hidden ?? false,
      created_at: child.created_at || parent?.created_at || "",
      stripe_session_id: parent?.stripe_session_id ?? null,
      tuition_total_cents: child.tuition_total_cents ?? null,
      transfer_credit_cents: child.transfer_credit_cents ?? null,
      balance_due_cents: child.balance_due_cents ?? null,
      // Paid-vs-comp classification travels with the child seat so the paid
      // minimum-enrollment count never mistakes a comp seat for tuition.
      is_complimentary: parent?.is_complimentary ?? false,
      complimentary_reason: parent?.complimentary_reason ?? null,
      discount_code: parent?.discount_code ?? null,
      discount_cents: parent?.discount_cents ?? null,
      original_price_cents: parent?.original_price_cents ?? null,
      is_test_record: parent?.is_test_record ?? false,
      removed_at: parent?.removed_at ?? null,
      operational_exception: parent?.operational_exception ?? null,
    } as RegistrationRow;
  });

  const legacyParentRoster = payments.filter((r) => {
    if (!aliases.has(String(r.class_id))) return false;
    if (!countsAsEnrolled(r)) return false;
    if (paymentsWithChildren.has(r.id)) return false;
    if (!sessionLabel) return true;
    // Explicit canonical assignment wins over raw historical labels.
    if (r.canonical_session_label) return sessionLabelMatches(r.canonical_session_label, sessionLabel);
    const labels = parseSelectedSessions(r.selected_sessions);
    return labels.some((label) => sessionLabelMatches(label, sessionLabel));
  });


  return [...childRoster, ...legacyParentRoster];
}

/**
 * Number of enrolled seats for a class + session occurrence. Includes any
 * manual admin reservations for that same session key. Manual reservations
 * are looked up against every alias so admin keys stay stable regardless of
 * whether a class is referenced by slug or UUID.
 */
export function enrollmentFor(
  payments: RegistrationRow[],
  classId: string,
  sessionLabel: string | null,
  children: RegistrationChildRow[] = [],
): number {
  const base = rosterFor(payments, classId, sessionLabel, children).length;
  let manual = 0;
  for (const alias of getClassAliases(classId)) {
    manual += sessionLabel
      ? MANUAL_SESSION_RESERVATIONS[`${alias}|${normalizeSessionLabel(sessionLabel)}`] ?? 0
      : MANUAL_RESERVATIONS[alias] ?? 0;
    if (manual) break;
  }
  return base + manual;
}

/** Raw interest rows for a class, alias-normalized (slug / db:slug / uuid). */
export function interestRowsFor(interest: WaitlistRow[], classId: string): WaitlistRow[] {
  const aliases = new Set(getClassAliases(classId));
  return interest.filter((i) => aliases.has(String(i.class_id)));
}

/**
 * CANONICAL interest metric: distinct FAMILIES who submitted an interest form
 * for this class. One family that submits twice (or once per child, or once
 * under `pe-class` and again under `db:pe-class`) is one interested family.
 *
 * Identity = lowercased email, falling back to the adult's name when a legacy
 * row has no email. Every admin and public surface must use this function so
 * an interest count can never differ between two pages.
 */
export function interestFamiliesFor(interest: WaitlistRow[], classId: string): number {
  const seen = new Set<string>();
  for (const i of interestRowsFor(interest, classId)) {
    const key =
      String(i.email ?? "").trim().toLowerCase() ||
      `name:${String(i.adult_name ?? "").trim().toLowerCase()}`;
    if (key === "name:") continue;
    seen.add(key);
  }
  return seen.size;
}

/** @deprecated Use `interestFamiliesFor` — raw row counts double-count families. */
export function waitlistCountFor(interest: WaitlistRow[], classId: string): number {
  return interestRowsFor(interest, classId).length;
}


/** Fetch every registration + interest row needed for enrollment math. */
export async function fetchEnrollmentData(): Promise<{
  payments: RegistrationRow[];
  interest: WaitlistRow[];
  children: RegistrationChildRow[];
}> {
  const [pRes, iRes, cRes] = await Promise.all([
    supabase
      .from("class_payments")
      .select(
        "id, class_id, adult_name, child_name, email, selected_sessions, amount_paid, payment_status, hidden, created_at, stripe_session_id, refunded_amount_cents, is_test_record, removed_at, operational_exception, operational_exception_note, canonical_session_label, tuition_total_cents, transfer_credit_cents, balance_due_cents, transfer_source_payment_id, admin_audit_note, is_complimentary, complimentary_reason, discount_code, discount_cents, original_price_cents",
      )
      .order("created_at", { ascending: false }),
    supabase
      .from("class_interest")
      .select("id, class_id, adult_name, child_name, email, created_at")
      .order("created_at", { ascending: false }),
    supabase
      .from("class_registration_children")
      .select(
        "id, class_payment_id, class_id, session_label, child_first_name, child_last_name, child_age, grade, notes, final_child_amount, tuition_total_cents, transfer_credit_cents, balance_due_cents, created_at",
      )
      .order("created_at", { ascending: false }),
  ]);
  return {
    payments: (pRes.data as RegistrationRow[]) ?? [],
    interest: (iRes.data as WaitlistRow[]) ?? [],
    children: (cRes.data as RegistrationChildRow[]) ?? [],
  };
}

/** UI helper: color tone for capacity indicator. */
export type CapacityTone = "empty" | "healthy" | "near" | "full";
export function capacityTone(enrolled: number, capacity: number): CapacityTone {
  if (capacity <= 0) return "healthy";
  const pct = enrolled / capacity;
  if (pct >= 1) return "full";
  if (pct >= 0.8) return "near";
  if (enrolled === 0) return "empty";
  return "healthy";
}
